import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import "react-quill-new/dist/quill.snow.css";
import { X, Paperclip, Wand, Plus } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import ReactQuill, { Quill } from "react-quill-new";
import ImageResize from "@mgreminger/quill-image-resize-module";
import "quill-mention/autoregister";
import { EntityStatus, EntityType } from "@repo/types/enums";
import { UpdateTemplateType } from "../../../types/enums/UpdateTemplateType";
import type { TemplateDTO } from "../../../types/dtos/TemplateDTO";
import type TextEditorProps from "../../../types/ui/TextEditorProps";
import type { KeyValueItem } from "@repo/types/ui/keyValuePairs";
import { KeyValueViewType } from "@repo/types/ui/keyValuePairs";
import type TextEditorViewModel from "../../../types/viewModels/TextEditorViewModel";
import type { ExecutionResponse } from "@repo/types/responses";
import useAddUpdateTemplate, {
  useGetTemplates,
} from "../../../hooks/queries/useTemplates";
import { useUserKeyValueItems } from "@repo/hooks/queries";
import { useUploadFile } from "../../../hooks/queries";
import KeyValueMenu from "../menu/KeyValueMenu";
import Button from "../button/Button";
import AddEditTemplate from "../../templates/AddEditTemplate";
import { ImagePlaceholderBlot } from "../../../helpers/reactQuillBlot";
import type { RecordId } from "@repo/types/ui/tables";

Quill.register("modules/imageResize", ImageResize);
Quill.register(ImagePlaceholderBlot);

const TextEditor: React.FC<TextEditorProps> = ({
  mentionsEnabled = false,
  templatesEnabled = false,
  attachmentsEnabled = false,
  submitButtonText = "Submit",
  onSubmit,
  children,
}) => {
  const [attachments, setAttachments] = useState<File[]>([]);
  const quillRef = React.useRef<ReactQuill>(null);

  const { data: templateKeyValueItems, isPending: isTemplatesLoading } =
    useGetTemplates((kv) =>
      kv.map(
        (template: TemplateDTO): KeyValueItem => ({
          viewType: KeyValueViewType.textOnly,
          Id: template.Id,
          Value: template.Name,
          Description: template.Content,
          classNames: "p-1 mb-1 hover:bg-gray-100",
        }),
      ),
    );

  const { data: userKeyValues } = useUserKeyValueItems();

  const { mutateAsync: uploadFile } = useUploadFile();

  const { mutateAsync: updateTemplate } = useAddUpdateTemplate();

  const handleTemplateDelete = async (
    id: RecordId,
  ): Promise<ExecutionResponse> => {
    const res = await updateTemplate({
      Id: id,
      Status: EntityStatus.Deleted,
      UpdateType: UpdateTemplateType.Status,
    });

    return res;
  };

  const baseUrl = import.meta.env.VITE_API_URL + "/File/GetFile";

  const handleTemplateChange = (id: number | undefined) => {
    const templateKeyValueItem = templateKeyValueItems?.find(
      (t) => t.Id === id,
    );
    if (!templateKeyValueItem) return;

    const currentContent = getValues("Content") || "";

    // 🔥 update editor immediately
    setValue(
      "Content",
      currentContent + (templateKeyValueItem.Description || ""),
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  };

  const {
    control,
    register,
    setValue,
    getValues,
    formState: { isValid },
  } = useFormContext<TextEditorViewModel>();

  const handleAttachments = (e: ChangeEvent<HTMLInputElement>) => {
    const newAttachments = Array.from(e.target.files || []);
    const updated = [...attachments, ...newAttachments];
    setAttachments(updated);
    setValue("Attachments", updated);
    e.target.value = "";
  };

  useEffect(() => {
    register("Content");
    register("Mentions");
    register("Attachments");
  }, [register]);

  const imageHandler = useCallback(async () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const range = quill.getSelection(true);
      const insertIndex = range?.index ?? quill.getLength();

      // 1️⃣ insert placeholder blot
      quill.insertEmbed(
        insertIndex,
        "image-placeholder",
        { width: 200, height: 100 },
        "user",
      );
      quill.setSelection(insertIndex + 1);

      try {
        // 2️⃣ upload
        const res = await uploadFile({ file, entityType: EntityType.None });
        const fileSrc = `${baseUrl}?filePath=${res.Para1}`;

        // 3️⃣ wait for image to actually load (off-DOM)
        await new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.src = fileSrc;
          img.onload = () => resolve();
          img.onerror = () => reject();
        });

        // 4️⃣ replace placeholder via Quill (IMPORTANT)
        quill.deleteText(insertIndex, 1, "user");
        quill.insertEmbed(insertIndex, "image", fileSrc, "user");
        quill.setSelection(insertIndex + 1);
      } catch {
        // cleanup placeholder on failure
        quill.deleteText(insertIndex, 1, "user");
      }
    };
  }, [uploadFile, baseUrl]);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ size: [] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["link", "image", "code-block"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      // ✅ NEW: modern image resize support
      imageResize: {
        handleStyles: {
          backgroundColor: "black",
        },
      },
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ["@"],
        source: (searchTerm: string, renderList: (values: any[]) => void) => {
          const values =
            userKeyValues?.map((u) => ({
              id: u.Id,
              value: u.Value,
            })) ?? [];

          renderList(
            values.filter((v) =>
              v.value.toLowerCase().includes(searchTerm.toLowerCase()),
            ),
          );
        },
        onSelect: (item: any, insertItem: (item: any) => void) => {
          insertItem(item);

          const prev = getValues("Mentions") ?? [];
          const updated = Array.from(new Set([...prev, item.id.toString()]));

          setValue("Mentions", updated, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
    }),
    [imageHandler, userKeyValues, getValues, setValue],
  );

  return (
    <>
      <form id="text-editor-form">
        {/* Editor Content */}
        <div className="max-h-125 overflow-auto">{children}</div>

        {/* Quill Editor */}
        <div className="mb-0 relative h-110">
          <Controller
            name="Content"
            control={control}
            rules={{
              required: "Content is required",
            }}
            render={({ field }) => (
              <ReactQuill
                ref={quillRef}
                value={field.value}
                onChange={field.onChange}
                modules={quillModules}
                className="h-99"
              />
            )}
          />
        </div>

        {/* Attachments */}
        {attachments && attachments.length > 0 && (
          <div className="px-4 pb-3">
            <div className="flex flex-wrap gap-2 mt-2">
              {attachments?.map((file, i) => (
                <span
                  key={i}
                  className="bg-gray-50 text-black-700 text-xs px-2 py-1 rounded border border-gray-200"
                >
                  <span className="font-medium">{file.name}</span>
                  <X
                    size={12}
                    className="inline ml-5 cursor-pointer hover:text-red-600"
                    onClick={() => {
                      const updatedAttachments = attachments.filter(
                        (_, index) => index !== i,
                      );
                      setAttachments(updatedAttachments);
                      setValue("Attachments", updatedAttachments);
                    }}
                  />
                </span>
              ))}
            </div>
          </div>
        )}
      </form>

      {/* Footer */}
      <div className="flex items-center gap-3 p-2 bg-gray-50 border-t border-gray-200">
        <Button
          size="sm"
          disabled={!isValid}
          type="submit"
          form="text-editor-form"
          onClick={onSubmit}
        >
          {submitButtonText}
        </Button>

        {attachmentsEnabled && (
          <label className="text-gray-500 hover:text-blue-600 cursor-pointer">
            <Paperclip size={16} />
            <input
              type="file"
              multiple
              onChange={handleAttachments}
              className="hidden"
            />
          </label>
        )}

        {(mentionsEnabled || (templatesEnabled && !isTemplatesLoading)) && (
          <div className="flex items-center gap-2 ml-auto">
            {mentionsEnabled && (
              <Button size="sm" variant="ghost" type="button">
                @Mention
              </Button>
            )}

            {templatesEnabled && !isTemplatesLoading && (
              <KeyValueMenu
                items={templateKeyValueItems}
                itemsDeletable={true}
                onDeleteClick={handleTemplateDelete}
                onValueChange={async (newValue: number) => {
                  handleTemplateChange(newValue);
                }}
                triggerRenderer={(_, open) => (
                  <div
                    className={`flex space-x-1 p-2 hover:bg-blue-300 ${
                      open ? "bg-blue-200 rounded" : ""
                    }`}
                  >
                    <Wand size={15} />
                    <div>Templates</div>
                  </div>
                )}
                customActionTriggerRenderer={(onClick) => (
                  <div
                    onClick={onClick}
                    className="flex w-full h-full mx-auto space-x-1 text-sm p-2 items-center justify-center hover:bg-gray-100"
                  >
                    <Plus size={15} /> <div>Save draft as new template</div>
                  </div>
                )}
                customActionTemplateRenderer={(onClose) => (
                  <AddEditTemplate onClose={onClose} />
                )}
                popupClassNames="w-80"
                positionerSide="left"
                positionerAlign="end"
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TextEditor;
